import React from "react"

import { BASE, COLOR, FORMATTING, JARGON, KEY_VALUES, KIND, RADIO, TAB, VIEW } from "../../modules/constants"
import { validateTableInfoPage } from "../../pages/companiesHouse/pageFunctions/commonFunctions"
import PropTypes from 'prop-types'

import { withStyles } from "@mui/styles"
import { useTheme } from "@mui/material/styles"
import TablePagination from '@mui/material/TablePagination'

import StarIcon from '@mui/icons-material/Star'
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined'
import SearchIcon from '@mui/icons-material/Search'
import LastPageIcon from '@mui/icons-material/LastPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import FirstPageIcon from '@mui/icons-material/FirstPage'

import Box from '../../components/ThemedBox'

import {
  IconButton,
  // TablePagination,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material'

const { border1 } = FORMATTING

const styles = () => ({
  titleBar: {
    minHeight: `${KEY_VALUES.TITLEBAR_HEIGHT}px`,
    width: "100%",
    backgroundColor: COLOR.BACKGROUND,
    position: "relative",
    display: "flex",
    flexWrap: "wrap",
    // flexDirection: "row",
    justifyContent: "center",
    paddingTop: 4,
    paddingBottom: 2,
    marginBottom: 0.5,
    borderBottom: border1,
    // boxShadow: `0px 3px 3px ${COLOR.LIGHT_GREY} inset`,
  },
  logoElement: {
    maxWidth: 250, // switch with portrait
    maxHeight: 24,
    marginTop: "auto",
    marginBottom: "auto",
    marginLeft: 10,
    marginRight: 10,
  },
})

function TablePaginationActions(props) {
  // console.log('TablePaginationActions props:', props)
  const theme = useTheme()
  const { classes, count, tableInfo, handleChangePage } = props
  const { page, rowsPerPage } = tableInfo

  const handleFirstPageButtonClick = event => handleChangePage(event, 0)
  const handleBackButtonClick = event => handleChangePage(event, page - 1)
  const handleNextButtonClick = event => handleChangePage(event, page + 1)
  const handleLastPageButtonClick = event => handleChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  const iconButtonSx = { paddingRight: 0 }

  return (
    <React.Fragment>
      <IconButton
        sx={{ ...iconButtonSx }}
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        sx={{ ...iconButtonSx }}
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        sx={{ ...iconButtonSx }}
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        sx={{ ...iconButtonSx }}
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </React.Fragment>
  )
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  handleChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
}

const TitleBar = props => {
  // console.log('TitleBar props:', props)
  const { advisers, advisersMeta, classes, companyNumber, features, isCompany, kind, lsePerson, lseProps, maxLseData, portrait, radio, searchIcon, showLogos, starIcon } = props
  const { handleOnClickFavourite, isFavourite, the350club } = props // Favourites constants
  const favouritesEnabled = features.Favourites
  const { count, handleChangeRowsPerPage, handleChangePage, nexusView } = props // Pagination constants
  const tableInfo = validateTableInfoPage({ ...props.tableInfo, count })
  const titleText = props.titleText

  // Boolean which is true if the current company/person is in the advisersMeta list - used to determine whether to show the "Clients" tab in the nexus view
  const isAdviser = isCompany && (advisersMeta ?? []).some(
    item => item.companyNumber === String(companyNumber)
  )

  const logoElement = logoSource => !!logoSource && showLogos
    ? (<img
      className={classes.logoElement}
      src={logoSource}
      alt="logo"
    // style={portrait ? { maxWidth: 150 } : {}}
    />)
    : null

  const handleChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Radio button value must be a string so we change this to an int
    // console.log('event.target.value:', event.target.value)
    props.handleChangeRadio(parseInt(event.target.value))
  }

  // Renders a radio "tab" label: bold + brand colour when selected, normal weight + black otherwise
  const radioLabel = (text, selected) =>
    <Typography
      variant="radioButton"
      style={{
        fontWeight: selected ? "bold" : "normal",
        color: selected ? COLOR.BRAND.STD : COLOR.BLACK,
      }}
    >
      {text}{" "}
    </Typography>

  // Boolean which is true if we are currently on lse companies tab
  const lseCompaniesTab = props.tabIndex === TAB[BASE.LSE.COMPANIES].INDEX
  const lseRolestab = props.tabIndex === TAB[BASE.LSE.ROLES].INDEX
  const lsePeopletab = props.tabIndex === TAB[BASE.LSE.PEOPLE].INDEX
  const favouritesTab = props.tabIndex === TAB[BASE.FAVOURITES].INDEX
  const companyTab = props.tabIndex === TAB[BASE.COMPANY].INDEX
  const personTab = props.tabIndex === TAB[BASE.PERSON].INDEX
  const nexusTab = companyTab || personTab
  // Boolean which determines whether the user can access board and governance data
  const boardAndGovernance = features?.PremiumCheckboxes // && !props.the350club

  // In portrait, the Board/Roles view toggle buttons render directly below this title bar (see NexusPage's
  // viewButtonsArea), so its usual bottom divider would sit right above them - suppress it there instead
  const hideBottomBorder = portrait && (kind === KIND.NEXUS.COMPANY.BOARD || kind === KIND.NEXUS.PERSON.ROLES)

  return (
    <Box component="div" variant={hideBottomBorder ? "titleBarPortrait" : "titleBar"} >

      {/* When searchIcon is true, show the search icon (search companiesHouse tab) */}
      {searchIcon && <SearchIcon fontSize="search" />}

      {starIcon && !the350club && /* (!portrait || favouritesEnabled) && */ <Tooltip
        placement={"bottom"}
        disableFocusListener
        title={favouritesEnabled
          ? isFavourite ? "click to remove from favourites" : "click to add to favourites"
          : "add/remove favourite - only available when user is logged in"
        }
      >
        <IconButton
          aria-label="star"
          onClick={() => handleOnClickFavourite(favouritesEnabled)}
          style={{ color: COLOR.BRAND.STD }}
        >
          {favouritesEnabled
            ? isFavourite
              ? (<StarIcon fontSize="medium" />)
              : (<StarBorderOutlinedIcon fontSize="medium" />)
            : (<StarBorderOutlinedIcon fontSize="medium" />)}
        </IconButton>
      </Tooltip>
      }

      {lseProps && logoElement(lseProps.logo)}

      <Typography variant="titleText"  >
        {titleText}
      </Typography>

      {/* If is lse companies tab and user does have access to board and governance data, show the radio buttons */}
      {lseCompaniesTab && boardAndGovernance &&
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={radio[BASE.LSE.COMPANIES].toString()} // numbers cannot be used as radio values, so we must convert to string
          onChange={handleChangeRadio}
          style={{ paddingLeft: 18 }}
        >
          <FormControlLabel
            // numbers cannot be used as radio values, so we must convert RADIO[BASE.LSE.COMPANIES].* to string
            value={RADIO[BASE.LSE.COMPANIES].OVERVIEW?.toString()} control={<Radio />}
            label={radioLabel(JARGON.OVERVIEW, radio[BASE.LSE.COMPANIES] === RADIO[BASE.LSE.COMPANIES].OVERVIEW)}
          // style={{height: 20}}
          />
          <FormControlLabel
            value={RADIO[BASE.LSE.COMPANIES].BOARD?.toString()} control={<Radio />}
            label={radioLabel(JARGON.BOARD, radio[BASE.LSE.COMPANIES] === RADIO[BASE.LSE.COMPANIES].BOARD)}
          // style={{height: 20}}
          />
          {<FormControlLabel
            value={RADIO[BASE.LSE.COMPANIES].COMMITTEES?.toString()} control={<Radio />} //
            label={radioLabel(JARGON.COMMITTEES, radio[BASE.LSE.COMPANIES] === RADIO[BASE.LSE.COMPANIES].COMMITTEES)}
          // style={{height: 20}}
          />}
          <FormControlLabel
            value={RADIO[BASE.LSE.COMPANIES].GOVERNANCE?.toString()} control={<Radio />} //
            label={radioLabel(JARGON.GOVERNANCE, radio[BASE.LSE.COMPANIES] === RADIO[BASE.LSE.COMPANIES].GOVERNANCE)}
          // style={{height: 20}}
          />
          <FormControlLabel
            value={RADIO[BASE.LSE.COMPANIES].DATES?.toString()} control={<Radio />} //
            label={radioLabel(JARGON.DATES, radio[BASE.LSE.COMPANIES] === RADIO[BASE.LSE.COMPANIES].DATES)}
          // style={{height: 20}}
          />
          {advisers > 0 && !the350club && <FormControlLabel
            value={RADIO[BASE.LSE.COMPANIES].ADVISERS?.toString()} control={<Radio />} //
            label={radioLabel(JARGON.ADVISERS, radio[BASE.LSE.COMPANIES] === RADIO[BASE.LSE.COMPANIES].ADVISERS)}
          // style={{height: 20}}
          />}
        </RadioGroup>
      }
      {/* If is lse lseRolestab */}
      {lseRolestab && boardAndGovernance &&
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={radio[BASE.LSE.ROLES].toString()} // numbers cannot be used as radio values, so we must convert to string
          onChange={handleChangeRadio}
          style={{ paddingLeft: 18 }}
        >
          <FormControlLabel
            value={RADIO[BASE.LSE.ROLES].BOARD.toString()} control={<Radio />}
            label={radioLabel(JARGON.BOARD, radio[BASE.LSE.ROLES] === RADIO[BASE.LSE.ROLES].BOARD)}
          // style={{height: 20}}
          />
          {<FormControlLabel
            value={RADIO[BASE.LSE.ROLES].COMMITTEES.toString()} control={<Radio />} //
            label={radioLabel(JARGON.COMMITTEES, radio[BASE.LSE.ROLES] === RADIO[BASE.LSE.ROLES].COMMITTEES)}
          // style={{height: 20}}
          />}

        </RadioGroup>
      }

      {/* If is lsePeopletab */}
      {lsePeopletab && boardAndGovernance &&
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={radio[BASE.LSE.PEOPLE].toString()} // numbers cannot be used as radio values, so we must convert to string
          onChange={handleChangeRadio}
          style={{ paddingLeft: 18 }}
        >
          <FormControlLabel
            value={RADIO[BASE.LSE.PEOPLE].BOARD.toString()} control={<Radio />}
            label={radioLabel(JARGON.BOARD, radio[BASE.LSE.PEOPLE] === RADIO[BASE.LSE.PEOPLE].BOARD)}
          // style={{height: 20}}
          />
          {<FormControlLabel
            value={RADIO[BASE.LSE.PEOPLE].COMMITTEES.toString()} control={<Radio />} //
            label={radioLabel(JARGON.COMMITTEES, radio[BASE.LSE.PEOPLE] === RADIO[BASE.LSE.PEOPLE].COMMITTEES)}
          // style={{height: 20}}
          />}

        </RadioGroup>
      }

      {/* nexus view radio buttons (dashboard/board) if lse company)*/}
      {nexusTab && lseProps && maxLseData &&
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={radio[BASE.COMPANY].toString()} // numbers cannot be used as radio values, so we must convert to string
          onChange={handleChangeRadio}
          style={{ paddingLeft: 25 }}
        >
          <FormControlLabel
            value={RADIO[BASE.COMPANY].DASHBOARD.toString()} control={<Radio />}
            label={radioLabel(JARGON.DASHBOARD, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].DASHBOARD)}
          />

          <FormControlLabel
            value={RADIO[BASE.COMPANY].BOARD.toString()} control={<Radio />}
            label={radioLabel(isCompany ? JARGON.BOARD : JARGON.ROLES, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].BOARD)}
          />

          {!the350club && <FormControlLabel
            value={RADIO[BASE.COMPANY].CONNECTIONS.toString()} control={<Radio />}
            label={radioLabel(JARGON.CONNECTIONS, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].CONNECTIONS)}
          />}

          {<FormControlLabel
            value={RADIO[BASE.COMPANY].COMMITTEES.toString()} control={<Radio />}
            label={radioLabel(JARGON.COMMITTEES, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].COMMITTEES)}
          />}

          <FormControlLabel
            value={RADIO[BASE.COMPANY].NEWS.toString()} control={<Radio />}
            label={radioLabel(JARGON.NEWS, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].NEWS)}
          />

          {isAdviser && <FormControlLabel
            value={RADIO[BASE.COMPANY].ADVISER_CLIENTS.toString()} control={<Radio />}
            label={radioLabel(`${JARGON.ADVISER_CLIENT}s`, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].ADVISER_CLIENTS)}
          />}
        </RadioGroup>
      }
      {/* nexus view radio buttons (dashboard/board) if NOT lse company, but is Adviser)*/}
      {nexusTab && !lseProps && isAdviser && maxLseData &&
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={radio[BASE.COMPANY].toString()} // numbers cannot be used as radio values, so we must convert to string
          onChange={handleChangeRadio}
          style={{ paddingLeft: 25 }}
        >

          <FormControlLabel
            value={RADIO[BASE.COMPANY].BOARD.toString()} control={<Radio />}
            label={radioLabel(isCompany ? JARGON.BOARD : JARGON.ROLES, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].BOARD)}
          />

          {!the350club && <FormControlLabel
            value={RADIO[BASE.COMPANY].CONNECTIONS.toString()} control={<Radio />}
            label={radioLabel(JARGON.CONNECTIONS, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].CONNECTIONS)}
          />}

          <FormControlLabel
            value={RADIO[BASE.COMPANY].ADVISER_CLIENTS.toString()} control={<Radio />}
            label={radioLabel(`${JARGON.ADVISER_CLIENT}s`, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].ADVISER_CLIENTS)}
          />
        </RadioGroup>
      }
      {/* nexus view radio buttons (board/connections) for a private company - ie. not an lse company and not an adviser */}
      {companyTab && !lseProps && !isAdviser && maxLseData &&
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={radio[BASE.COMPANY].toString()} // numbers cannot be used as radio values, so we must convert to string
          onChange={handleChangeRadio}
          style={{ paddingLeft: 25 }}
        >
          <FormControlLabel
            value={RADIO[BASE.COMPANY].BOARD.toString()} control={<Radio />}
            label={radioLabel(JARGON.BOARD, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].BOARD)}
          />

          {!the350club && <FormControlLabel
            value={RADIO[BASE.COMPANY].CONNECTIONS.toString()} control={<Radio />}
            label={radioLabel(JARGON.CONNECTIONS, radio[BASE.COMPANY] === RADIO[BASE.COMPANY].CONNECTIONS)}
          />}
        </RadioGroup>
      }
      {/* If is Favourites tab */}
      {favouritesTab && favouritesEnabled &&
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={radio[BASE.FAVOURITES].toString()} // numbers cannot be used as radio values, so we must convert to string
          onChange={handleChangeRadio}
          style={{ paddingLeft: 18 }}
        >
          <FormControlLabel
            value={RADIO[BASE.FAVOURITES].COMPANIES.toString()} control={<Radio />}
            label={radioLabel(JARGON.COMPANIES, radio[BASE.FAVOURITES] === RADIO[BASE.FAVOURITES].COMPANIES)}
          />
          {<FormControlLabel
            value={RADIO[BASE.FAVOURITES].PEOPLE.toString()} control={<Radio />} //
            label={radioLabel(JARGON.PEOPLE, radio[BASE.FAVOURITES] === RADIO[BASE.FAVOURITES].PEOPLE)}
          />}

        </RadioGroup>
      }

      {/* nexus view radio buttons (dashboard/board) if lse person)*/}
      {nexusTab && lsePerson && maxLseData &&
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={radio[BASE.PERSON].toString()} // numbers cannot be used as radio values, so we must convert to string
          onChange={handleChangeRadio}
          style={{ paddingLeft: 25 }}
        >
          <FormControlLabel
            value={RADIO[BASE.PERSON].ROLES.toString()} control={<Radio />}
            label={radioLabel(JARGON.ROLES, radio[BASE.PERSON] === RADIO[BASE.PERSON].ROLES)}
          />

          {!the350club && <FormControlLabel
            value={RADIO[BASE.PERSON].CONNECTIONS.toString()} control={<Radio />}
            label={radioLabel(JARGON.CONNECTIONS, radio[BASE.PERSON] === RADIO[BASE.PERSON].CONNECTIONS)}
          />}

          {<FormControlLabel
            value={RADIO[BASE.PERSON].COMMITTEES.toString()} control={<Radio />}
            label={radioLabel(JARGON.COMMITTEES, radio[BASE.PERSON] === RADIO[BASE.PERSON].COMMITTEES)}
          />}

          <FormControlLabel
            value={RADIO[BASE.PERSON].NEWS.toString()} control={<Radio />}
            label={radioLabel(JARGON.NEWS, radio[BASE.PERSON] === RADIO[BASE.PERSON].NEWS)}
          />
        </RadioGroup>
      }

      {/* nexus view radio buttons (board roles/connections) for an officer of a private company - ie. not an lse person */}
      {personTab && !lsePerson && maxLseData &&
        <RadioGroup
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
          value={radio[BASE.PERSON].toString()} // numbers cannot be used as radio values, so we must convert to string
          onChange={handleChangeRadio}
          style={{ paddingLeft: 25 }}
        >
          <FormControlLabel
            value={RADIO[BASE.PERSON].ROLES.toString()} control={<Radio />}
            label={radioLabel(JARGON.ROLES, radio[BASE.PERSON] === RADIO[BASE.PERSON].ROLES)}
          />

          {!the350club && <FormControlLabel
            value={RADIO[BASE.PERSON].CONNECTIONS.toString()} control={<Radio />}
            label={radioLabel(JARGON.CONNECTIONS, radio[BASE.PERSON] === RADIO[BASE.PERSON].CONNECTIONS)}
          />}
        </RadioGroup>
      }

      {count > KEY_VALUES.DEFAULT_ROWS_PER_PAGE && nexusView !== VIEW.D3 && nexusView !== VIEW.TIMELINE && <TablePagination
        // classes={{ root: classes.pagination }}
        style={{ paddingTop: 5, paddingRight: 18, paddingBottom: 0, color: tableInfo.page > 0 ? COLOR.WARN : COLOR.BLACK }}
        rowsPerPageOptions={portrait ? [] : []} // [100, 200]}
        // labelRowsPerPage={portrait ? '' : 'rows/page'}
        component="div"
        count={count}
        rowsPerPage={portrait ? KEY_VALUES.DEFAULT_ROWS_PER_PAGE : tableInfo.rowsPerPage}
        page={tableInfo.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={() => TablePaginationActions({ ...props, tableInfo })}
      />}

    </Box>
  )
}

export default withStyles(styles)(TitleBar)
